import React, { useCallback } from 'react';
import { usePathname, useRouter } from 'expo-router';

import type { AddressType } from '@sg/garnish';
import type { DeliveryOrderDetailInput } from '@sg/graphql-schema';

import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavigateToMenu = () => {
  const router = useRouter();
  const pathname = usePathname();

  const shouldDismissAll = !pathname.includes('/locations');

  const handleGoToMenu = React.useCallback(
    async (restaurantSlug: string, category?: string) => {
      if (shouldDismissAll && router.canDismiss()) {
        router.dismissAll();
      }

      router.navigate({
        pathname: '/[restaurantSlug]/menu',
        params: { restaurantSlug, ...(category ? { category } : {}) },
      });
    },
    [shouldDismissAll, router],
  );

  const handleGoToDeliveryMenu = React.useCallback(
    async (addressId: string, category?: string) => {
      if (shouldDismissAll && router.canDismiss()) {
        router.dismissAll();
      }

      router.navigate({
        pathname: '/delivery/[addressId]/menu',
        params: { addressId, ...(category ? { category } : {}) },
      });
    },
    [shouldDismissAll, router],
  );

  // ─── In Flight Delivery Orders ───────────────────────────────────

  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  return useCallback(
    ({
      orderType,
      restaurantSlug = '',
      deliveryOrderDetail = undefined,
      category,
    }: UseNavigateToMenuProps) => {
      if (['pickup', 'outpost'].includes(orderType)) {
        void handleGoToMenu(restaurantSlug, category);

        return;
      }

      // Block going to delivery menu when there is an in-flight delivery order.
      if (inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      const deliveryType = deliveryOrderDetail as Readonly<{
        address: { id: string };
      }>;
      const addressId = deliveryOrderDetail?.addressId ?? deliveryType?.address?.id;

      if (orderType === 'delivery' && addressId) {
        void handleGoToDeliveryMenu(addressId, category);
      }
    },
    [
      inFlightDeliveryOrderId,
      handleGoToDeliveryMenu,
      handleGoToMenu,
      navigateToDeliveryOrderInFlightScreen,
    ],
  );
};

export type UseNavigateToMenuProps = Readonly<{
  orderType: AddressType;
  restaurantId?: string;
  restaurantSlug?: string;
  restaurantName?: string;
  deliveryAddressName?: string;
  deliveryOrderDetail?: Partial<DeliveryOrderDetailInput>;
  referrer?: MenuScreenReferrer;
  category?: string;
}>;

type MenuScreenReferrer = 'bag';
